import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ProfileForm from "@/components/ProfileForm";
import MeritCard from "@/components/MeritCard";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import WeeklyView from "@/components/WeeklyView";
import ChartView from "@/components/ChartView";
import TaiSuiView from "@/components/TaiSuiView";
import { SealBadge } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { FortuneRadar } from "@/components/FortuneRadar";
import { ShichenDial } from "@/components/ShichenDial";
import { SmartMatch } from "@/components/SmartMatch";
import { DailyHexagram } from "@/components/DailyHexagram";
import {
  Sparkles, Palette, Compass, Hash, Users, AlertTriangle, Ban, Lightbulb,
  Wallet, Sun, Wind, ScrollText, ArrowRight, Briefcase, HeartPulse, Heart, Gauge, Crown, CalendarDays,
  Coins, Clock, CheckCircle2, ListChecks, Gift,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function LevelCard({ lang, points }) {
  const [info, setInfo] = useState(null);
  const [congrats, setCongrats] = useState(null);
  const load = useCallback(() => {
    api.get("/me/level").then((r) => {
      setInfo(r.data);
      if ((r.data.congrats || []).length) {
        setCongrats(r.data.congrats[r.data.congrats.length - 1]);
        api.post("/me/level/seen").catch(() => {});
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, points]);
  if (!info) return null;
  const lv = info.level;
  const zh = lang === "zh";
  return (
    <Card className="border-[hsl(var(--brand-gold))] mb-6" data-testid="level-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-[hsl(var(--brand-gold))]" />
            <span className="font-display text-lg font-semibold">{zh ? "功德等级" : "Merit Level"}：{zh ? lv.label_zh : lv.label_en}</span>
          </div>
          {info.vouchers.length > 0 && (
            <Badge className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" data-testid="level-voucher-count">
              <Gift className="h-3.5 w-3.5 mr-1" />{zh ? `${info.vouchers.length} 张礼券` : `${info.vouchers.length} vouchers`}
            </Badge>
          )}
        </div>
        {!lv.is_max ? (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{Number(lv.points).toFixed(0)} {zh ? "功德点" : "pts"}</span>
              <span data-testid="level-to-next">{zh ? `距 ${lv.next_zh} 还差 ${Number(lv.to_next).toFixed(0)} 点` : `${Number(lv.to_next).toFixed(0)} to ${lv.next_en}`}</span>
            </div>
            <Progress value={lv.progress} className="h-2" />
          </div>
        ) : (
          <p className="mt-2 text-sm text-[hsl(var(--brand-bronze))]">{zh ? "已达最高等级 · 大德，感恩您的功德！" : "Highest tier reached — thank you!"}</p>
        )}
        {info.vouchers.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {info.vouchers.map((v) => (
              <div key={v.code} className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] px-2.5 py-1.5 text-xs" data-testid={`level-voucher-${v.code}`}>
                <span className="font-semibold text-[hsl(var(--brand-bronze))]">{zh ? v.label_zh : v.label_en}</span>
                <span className="ml-1.5 font-mono-num text-muted-foreground">{v.code}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <Dialog open={!!congrats} onOpenChange={(o) => { if (!o) setCongrats(null); }}>
        <DialogContent className="max-w-sm text-center" data-testid="levelup-dialog">
          <DialogHeader><DialogTitle className="text-center">{zh ? "🎉 恭喜升级！" : "🎉 Level Up!"}</DialogTitle></DialogHeader>
          {congrats && (
            <div className="space-y-2">
              <Crown className="h-12 w-12 mx-auto text-[hsl(var(--brand-gold))]" />
              <p className="text-lg font-semibold">{zh ? `您已晋升为「${congrats.label_zh}」` : `You reached ${congrats.label_en}`}</p>
              <p className="text-sm text-muted-foreground">{zh ? `恭喜获得 RM${congrats.reward_rm} 折扣礼券，可在商城结账时使用！` : `You earned an RM${congrats.reward_rm} discount voucher for the shop!`}</p>
              <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" onClick={() => setCongrats(null)} data-testid="levelup-ok">{zh ? "太好了" : "Great"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Tile({ icon: Icon, label, children, testid, className = "" }) {
  return (
    <Card className={`border-border bg-card ${className}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))] mb-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[hsla(var(--brand-gold-soft),0.5)]">
            <Icon className="h-3.5 w-3.5 text-[hsl(var(--brand-bronze))]" />
          </span>
          <span className="text-sm font-bold text-foreground">{label}</span>
        </div>
        <div className="text-sm text-foreground/90 leading-6">{children}</div>
      </CardContent>
    </Card>
  );
}

function Chips({ items }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(items || []).map((x, i) => (
        <span key={i} className="rounded-full bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))] px-2.5 py-1 text-xs font-semibold">{x}</span>
      ))}
    </div>
  );
}

// Tile that shows a 0-100 index bar plus a descriptive text.
function IndexTile({ icon: Icon, label, value, text, testid }) {
  const v = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  return (
    <Card className="border-border bg-card" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))]">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[hsla(var(--brand-gold-soft),0.5)]">
              <Icon className="h-3.5 w-3.5 text-[hsl(var(--brand-bronze))]" />
            </span>
            <span className="text-sm font-bold text-foreground">{label}</span>
          </div>
          <span className="font-mono-num text-xl font-bold text-[hsl(var(--brand-vermillion))]" data-testid={`${testid}-value`}>{v}</span>
        </div>
        <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden mb-2">
          <div className="h-full bg-[hsl(var(--brand-gold))] transition-[width] duration-300" style={{ width: `${v}%` }} />
        </div>
        <div className="text-sm text-foreground/90 leading-6">{text}</div>
      </CardContent>
    </Card>
  );
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoted, setPromoted] = useState([]);
  const [services, setServices] = useState([]);
  const [selDate, setSelDate] = useState(() => new Date());

  const todayStr = ymd(new Date());
  const isToday = ymd(selDate) === todayStr;

  const loadFortune = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/fortune/today", { params: { lang, date: ymd(selDate) } });
      setData(res.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || "error" });
    } finally {
      setLoading(false);
    }
  }, [lang, selDate]);

  useEffect(() => { loadFortune(); }, [loadFortune]);
  useEffect(() => {
    api.get("/projects", { params: { promoted: true } }).then((r) => setPromoted(r.data.filter((p) => p.category !== "service").slice(0, 3))).catch(() => {});
    api.get("/projects", { params: { category: "service" } }).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const incomplete = data && data.profile_complete === false;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{user?.name}</h1>
          <p className="text-muted-foreground text-sm">{t("dashboard.greeting")}</p>
        </div>
        <Card className="border-border">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center">
              <Wallet className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("dashboard.wallet")}</div>
              <div className="font-mono-num text-lg font-bold">{Number(user?.points || 0).toFixed(2)} <span className="text-xs">{t("common.pointsUnit")}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <LevelCard lang={lang} points={user?.points} />

      <Tabs defaultValue="chart" data-testid="chart-tabs">
        <TabsList className="mb-4 flex flex-wrap h-auto">
          <TabsTrigger value="chart" data-testid="tab-chart">{t("dashboard.tabChart")}</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">{t("dashboard.tabDaily")}</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">{t("dashboard.tabWeekly")}</TabsTrigger>
          <TabsTrigger value="taisui" data-testid="tab-taisui">{t("dashboard.tabTaisui")}</TabsTrigger>
        </TabsList>

        <TabsContent value="daily">
      {/* Date selector */}
      {!incomplete && !data?.error && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="dashboard-date-selector">
          <span className="text-xs text-muted-foreground">{t("dashboard.selectDate")}</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="border-[hsl(var(--brand-gold))] font-mono-num" data-testid="dashboard-date-trigger">
                <CalendarDays className="h-4 w-4 mr-1.5 text-[hsl(var(--brand-bronze))]" />
                {ymd(selDate)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card" align="start">
              <Calendar
                mode="single"
                selected={selDate}
                onSelect={(d) => d && setSelDate(d)}
                initialFocus
                data-testid="dashboard-calendar"
              />
            </PopoverContent>
          </Popover>
          {!isToday && (
            <Button variant="ghost" size="sm" className="text-[hsl(var(--brand-vermillion))]" onClick={() => setSelDate(new Date())} data-testid="dashboard-back-today">
              {t("dashboard.today")}
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid md:grid-cols-12 gap-4">
          <Skeleton className="h-64 md:col-span-8 rounded-xl" />
          <Skeleton className="h-64 md:col-span-4 rounded-xl" />
        </div>
      ) : incomplete ? (
        <Card className="border-border max-w-xl mx-auto">
          <CardContent className="p-6">
            <div className="rounded-lg bg-[hsl(var(--brand-jade-mist))] p-3 text-sm mb-4">{t("profileForm.reminder")}</div>
            <h2 className="font-display text-xl font-semibold mb-4">{t("profileForm.title")}</h2>
            <ProfileForm onSaved={async () => { await refreshUser(); loadFortune(); }} />
          </CardContent>
        </Card>
      ) : data?.error ? (
        <Card className="border-border"><CardContent className="p-8 text-center">
          <p className="text-muted-foreground">{data.error}</p>
          <Button className="mt-4" onClick={loadFortune}>{lang === "zh" ? "重试" : "Retry"}</Button>
        </CardContent></Card>
      ) : data?.fortune ? (
        <>
        <div className="grid md:grid-cols-12 gap-4">
          {/* Left: fortune bento */}
          <div className="md:col-span-8 space-y-4">
            <Card className="border-border bg-card gold-rule overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <SealBadge>{isToday ? t("dashboard.today") : data.date}</SealBadge>
                    <span className="font-display text-lg font-semibold">{isToday ? t("dashboard.todayFortune") : t("dashboard.fortuneOf")}</span>
                    {data.date && <span className="font-mono-num text-xs text-muted-foreground">{data.date}</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{t("dashboard.score")}</div>
                    <div className="font-mono-num text-3xl font-bold text-[hsl(var(--brand-vermillion))]">{data.fortune.score}</div>
                  </div>
                </div>
                <p className="text-[15px] leading-7 text-foreground/90">{data.fortune.overall_summary}</p>
                {data.fortune.master_advice && (
                  <div className="mt-3 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.16)] p-3" data-testid="dashboard-master-advice">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--brand-bronze))] mb-1">
                      <Sparkles className="h-4 w-4" />{lang === "zh" ? "师傅参考 · CiAO Master" : "CiAO Master"}
                    </div>
                    <p className="text-sm leading-7 text-foreground/90">{data.fortune.master_advice}</p>
                  </div>
                )}
                {data.fortune.lucky_tip && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-[hsl(var(--secondary))] p-3">
                    <Lightbulb className="h-4 w-4 text-[hsl(var(--brand-bronze))] mt-0.5 shrink-0" />
                    <span className="text-sm">{data.fortune.lucky_tip}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FortuneRadar fortune={data.fortune} />
              <ShichenDial shichen={data.shichen} luckyHours={data.fortune.lucky_hours} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Tile icon={Sun} label={t("dashboard.overall")} testid="dashboard-fortune-tile-daily">{data.fortune.daily_fortune}</Tile>
              <Tile icon={ScrollText} label={t("dashboard.bazi")} testid="dashboard-fortune-tile-bazi">{data.fortune.bazi_fortune}</Tile>
              <Tile icon={Wind} label={t("dashboard.energy")} testid="dashboard-fortune-tile-energy">{data.fortune.today_energy}</Tile>
              <IndexTile icon={Gauge} label={t("dashboard.luck")} value={data.fortune.luck_index} text="" testid="dashboard-fortune-tile-luck" />
              <IndexTile icon={Briefcase} label={t("dashboard.career")} value={data.fortune.career_index} text={data.fortune.career} testid="dashboard-fortune-tile-career" />
              <IndexTile icon={HeartPulse} label={t("dashboard.health")} value={data.fortune.health_index} text={data.fortune.health} testid="dashboard-fortune-tile-health" />
              <IndexTile icon={Heart} label={t("dashboard.love")} value={data.fortune.love_index} text={data.fortune.love} testid="dashboard-fortune-tile-love" />
              {(data.fortune.wealth || data.fortune.wealth_index) ? (
                <IndexTile icon={Coins} label={t("dashboard.wealth")} value={data.fortune.wealth_index} text={data.fortune.wealth} testid="dashboard-fortune-tile-wealth" />
              ) : null}
              <Tile icon={Users} label={t("dashboard.benefactor")} testid="dashboard-fortune-tile-benefactor">
                <span className="text-lg font-semibold text-[hsl(var(--brand-vermillion))]">{data.fortune.benefactor_zodiac}</span>
              </Tile>
              <Tile icon={Palette} label={t("dashboard.colors")} testid="dashboard-fortune-tile-colors"><Chips items={data.fortune.lucky_colors} /></Tile>
              <Tile icon={Compass} label={t("dashboard.directions")} testid="dashboard-fortune-tile-directions"><Chips items={data.fortune.lucky_directions} /></Tile>
              <Tile icon={Hash} label={t("dashboard.numbers")} testid="dashboard-fortune-tile-numbers"><Chips items={(data.fortune.lucky_numbers || []).map(String)} /></Tile>
              <Tile icon={AlertTriangle} label={t("dashboard.cautions")} testid="dashboard-fortune-tile-cautions">
                <ul className="list-disc pl-4 space-y-1">{(data.fortune.cautions || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
              </Tile>
              {(data.fortune.dos || []).length > 0 && (
                <Tile icon={CheckCircle2} label={t("dashboard.dos")} testid="dashboard-fortune-tile-dos">
                  <ul className="list-disc pl-4 space-y-1">{(data.fortune.dos || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
                </Tile>
              )}
              <Tile icon={Ban} label={t("dashboard.avoid")} testid="dashboard-fortune-tile-avoid">
                <ul className="list-disc pl-4 space-y-1">{(data.fortune.avoid || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
              </Tile>
              {(data.fortune.lucky_hours || []).length > 0 && (
                <Tile icon={Clock} label={t("dashboard.luckyHours")} testid="dashboard-fortune-tile-hours" className="sm:col-span-2">
                  <ul className="list-disc pl-4 space-y-1">{(data.fortune.lucky_hours || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
                </Tile>
              )}
              {(data.fortune.action_advice || []).length > 0 && (
                <Tile icon={ListChecks} label={t("dashboard.actionAdvice")} testid="dashboard-fortune-tile-action" className="sm:col-span-2">
                  <ul className="list-disc pl-4 space-y-1">{(data.fortune.action_advice || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
                </Tile>
              )}
            </div>
          </div>

          {/* Right: bazi + recommended */}
          <div className="md:col-span-4 space-y-4">
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3"><ScrollText className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("dashboard.myBazi")}</span></div>
                {data.bazi && (
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "zh" ? "生肖" : "Zodiac"}</span><span className="font-semibold">{data.bazi.zodiac}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "zh" ? "农历" : "Lunar"}</span><span>{data.bazi.lunar_date}</span></div>
                    <div className="pt-2"><span className="text-muted-foreground text-xs">{t("dashboard.pillars")}</span>
                      <div className="grid grid-cols-4 gap-1 mt-1 text-center">
                        {["year", "month", "day", "time"].map((k) => (
                          <div key={k} className="rounded-md bg-[hsl(var(--secondary))] py-2">
                            <div className="font-display font-bold">{data.bazi.pillars[k]}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {promoted.length > 0 && (
              <SmartMatch fortune={data.fortune} pool={promoted} />
            )}
            <DailyHexagram autoLoad />
          </div>
        </div>

        {services.length > 0 && (
          <div className="mt-8" data-testid="dashboard-services-section">
            <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><span className="font-display text-xl font-semibold">{t("dashboard.servicesTitle")}</span></div>
            <p className="text-sm text-muted-foreground mb-4">{t("dashboard.servicesDesc")} · {t("service.deliverNote")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((p) => <MeritCard key={p.id} project={p} />)}
            </div>
          </div>
        )}
        </>
      ) : null}
        </TabsContent>

        <TabsContent value="weekly">
          <WeeklyView />
        </TabsContent>

        <TabsContent value="chart">
          <ChartView />
        </TabsContent>

        <TabsContent value="taisui">
          <TaiSuiView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
